// screens/PersonalNotes.tsx — the Personal tab's Notes sub-tab (spec
// `spec-the-cockpit-personal-tab-and-the-01b-nav-build-1-2026-10-08` §2e).
//
// The personal pack's `source` records — what the Knot captured and what was
// typed here — newest first, the text shown. Opening one is the EXISTING
// record editor (lib/recordEditor), and "New note" creates a `source` through
// the existing records API. No new primitive: a note is a source whose kind
// is 'note'.
//
// ⚑ NOT HouseholdNotes.tsx. That screen reads actor_assist's `aa_source` and
// is that pack's alone (route.ts SCREEN_OWNERS `notes`); the cockpit's
// capture type is `source` (verticals/personal captureSourceType).
import { useEffect, useState } from 'preact/hooks';
import { createRecord, listRecordsByType, type RecordRow } from '../lib/api';
import { bumpRecordsVersion, openRecordEditor, useRecordsVersion } from '../lib/recordEditor';
import { failureOf } from './personalShared';
import './PersonalNotes.css';

const PAGE = 200;

function textOf(r: RecordRow): string {
  const f = r.fields;
  const raw = typeof f.raw_text === 'string' ? f.raw_text.trim() : '';
  if (raw) return raw;
  const name = typeof f.filename === 'string' ? f.filename.trim() : '';
  return name || r.display_name || '(no text)';
}

function whenOf(r: RecordRow): string {
  const at = typeof r.fields.received_at === 'string' ? r.fields.received_at : r.created_at;
  return new Date(at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function kindOf(r: RecordRow): string | null {
  const k = typeof r.fields.kind === 'string' ? r.fields.kind.trim() : '';
  return k && k !== 'note' ? k : null;
}

export function PersonalNotes({ tenantId }: { tenantId: string }) {
  const version = useRecordsVersion();
  const [rows, setRows] = useState<RecordRow[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [writing, setWriting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listRecordsByType(tenantId, 'source', undefined, { limit: PAGE })
      .then((rs) => {
        if (cancelled) return;
        // The records route answers oldest-first (its own contract); newest
        // first is this screen's order, so it reorders here.
        const sorted = [...rs].sort((a, b) => b.created_at.localeCompare(a.created_at));
        setRows(sorted);
        setFailure(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setFailure(failureOf(err));
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, version]);

  async function keep(e: Event) {
    e.preventDefault();
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      await createRecord(tenantId, 'source', { kind: 'note', raw_text: text, received_at: new Date().toISOString() });
      setDraft('');
      setWriting(false);
      const head = text.length > 48 ? `${text.slice(0, 48)}…` : text;
      setReceipt(`Kept “${head}”.`);
      bumpRecordsVersion();
    } catch (err) {
      setReceipt(`Not kept — ${failureOf(err)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="pnotes-shell">
      <div class="pnotes">
        {receipt ? (
          <div class="personal__receipt" role="status">
            {receipt}
          </div>
        ) : null}
        {failure ? <div class="personal__failure">The notes could not be read — {failure}</div> : null}
        {rows === null && !failure ? <div class="pnotes__skeleton" aria-hidden="true" /> : null}
        {rows && rows.length === 0 ? <div class="personal__empty">No notes yet.</div> : null}
        {rows && rows.length > 0 ? (
          <ul class="pnotes__list">
            {rows.map((r) => {
              const kind = kindOf(r);
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    class="pnotes__row"
                    onClick={() => openRecordEditor({ mode: 'edit', typeKey: 'source', id: r.id })}
                  >
                    <span class="pnotes__text">{textOf(r)}</span>
                    <span class="pnotes__when">
                      {whenOf(r)}
                      {kind ? ` · ${kind}` : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
      {writing ? (
        <form class="pnotes__new" onSubmit={keep}>
          <textarea
            class="pnotes__draft"
            rows={4}
            value={draft}
            aria-label="New note"
            onInput={(e) => setDraft(e.currentTarget.value)}
          />
          <div class="pnotes__new-row">
            <button type="submit" class="personal__primary" disabled={busy || draft.trim() === ''}>
              Keep note
            </button>
            <button
              type="button"
              class="personal__secondary"
              onClick={() => {
                setWriting(false);
                setDraft('');
              }}
            >
              Close
            </button>
          </div>
        </form>
      ) : (
        <div class="personal__actions">
          <button type="button" class="personal__primary" onClick={() => setWriting(true)}>
            New note
          </button>
        </div>
      )}
    </div>
  );
}
